import { Dayjs, ManipulateType } from 'dayjs';
import { Recurrence } from '../types';

const dayjsUnitByRecurrenceFreq: Record<Recurrence['freq'], ManipulateType> = {
    WEEKLY: 'weeks',
    MONTHLY: 'months',
    YEARLY: 'years',
};

export const END_OF_MONTH_THRESHOLD_DATE = 29;

export const getNextRecurrence = ({
    dtStart,
    recurrence,
    dtOnOrAfter,
}: {
    dtStart: Dayjs;
    recurrence: Recurrence;
    dtOnOrAfter: Dayjs;
}): Dayjs => {
    // If start date is after on-or-after date, return start date
    if (dtStart.isAfter(dtOnOrAfter)) return dtStart;

    // Determine floating-point number of recurrences counting back to dtStart
    const nDiff = getNumberOfRecurrences(dtStart, dtOnOrAfter, recurrence);

    // Calculate number of recurrences to count forward
    const unit = dayjsUnitByRecurrenceFreq[recurrence.freq];
    const nNext = Number.isSafeInteger(nDiff) ? nDiff : Math.floor(nDiff) + 1;

    // If a monthly/yearly recurrence and dtStart's date might not exist in
    // certain months -- eg 29th, 30th, 31st -- attempt to get as close to the
    // date in the target month as possible.
    //
    // Ex: dtStart = 2022-01-30, dtOnOrAfter = 2022-02-01, returns 2022-02-28
    //
    // This is necessary since Dayjs' .add() logic will incorrectly bump to the
    // *next* month in this situation.
    if (
        (recurrence.freq === 'MONTHLY' || recurrence.freq === 'YEARLY') &&
        dtStart.date() >= END_OF_MONTH_THRESHOLD_DATE
    ) {
        const dtTargetMonth = dtStart
            .startOf('month')
            .add(recurrence.interval * nNext, unit);
        const dtEndOfTargetMonth = dtTargetMonth.endOf('month');

        if (dtEndOfTargetMonth.date() < dtStart.date()) {
            return dtEndOfTargetMonth;
        } else {
            return dtTargetMonth.set('date', dtStart.date());
        }
    }

    // Otherwise, just return the next recurrence
    return dtStart.add(recurrence.interval * nNext, unit);
};

/**
 * Do not reuse this function outside of getNextRecurrence! The only intended use
 * case is overriding the Dayjs.diff() logic for monthly calculations.
 */
const getNumberOfRecurrences = (
    from: Dayjs,
    to: Dayjs,
    recurrence: Recurrence
) => {
    if (recurrence.freq === 'MONTHLY') {
        // Override Dayjs.diff() logic
        //   dayjs(`2023-03-27`).diff(dayjs(`2023-02-28`),'months',true) // 0.964
        //   dayjs(`2023-03-28`).diff(dayjs(`2023-02-28`),'months',true) // 1
        //   dayjs(`2023-03-31`).diff(dayjs(`2023-02-28`),'months',true) // 1  :(
        const nDiff =
            (to.get('year') - from.get('year')) * 12 +
            (to.get('month') - from.get('month')) +
            // this calendar math doesn't have a "correct" answer, but dividing
            // by 31 produces a floating-point value which is all we need here
            (to.get('date') - from.get('date')) / 31;
        return nDiff / recurrence.interval;
    }
    if (recurrence.freq === 'YEARLY') {
        // Override Dayjs.diff() logic
        //   dayjs(`2024-02-27`).diff(dayjs(`2023-02-28`),'years',true) // 0.997
        //   dayjs(`2024-02-28`).diff(dayjs(`2023-02-28`),'years',true) // 1
        //   dayjs(`2024-02-29`).diff(dayjs(`2023-02-28`),'years',true) // 1  :(
        const nDiff =
            to.get('year') -
            from.get('year') +
            (to.get('month') - from.get('month')) / 12 +
            // this calendar math doesn't have a "correct" answer, but dividing
            // by 366 produces a floating-point value which is all we need here
            (to.get('date') - from.get('date')) / 366;
        return nDiff / recurrence.interval;
    }

    const unit = dayjsUnitByRecurrenceFreq[recurrence.freq];
    const nDiff = to.diff(from, unit, true);
    return nDiff / recurrence.interval;
};
